import type { components } from "@countdown/api-client";
import { type ReactElement, type ReactNode, useEffect } from "react";
import { CircleHelp, LogOut } from "lucide-react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router";

import { SideNav } from "@/components/layout/SideNav";
import { NotificationBell } from "@/components/NotificationBell";
import { SaveBarProvider } from "@/components/SaveBar";
import { SessionRenewalNotice } from "@/components/SessionRenewalNotice";
import { SignInScreen, useForgetSignInMarkers } from "@/components/SignIn";
import { TenantMenu } from "@/components/TenantMenu";
import { Button } from "@/components/ui/button";
import {
  LOAD_FAILED,
  ROLE_MISSING,
  SESSION_LOST,
  SIGN_IN_PATH,
  loadOverview,
  loadSession,
  signOut,
  useResource,
} from "@/lib/api";
import { helpAnchor } from "@/lib/help";
import { notArchived } from "@/lib/labels";
import { ActionPage, FieldPlan } from "@/pages/ActionPage";
import { ActionDesignPage } from "@/pages/ActionDesignPage";
import { ActionSettingsPage } from "@/pages/ActionSettingsPage";
import { ActionsPage } from "@/pages/ActionsPage";
import { AuditPage } from "@/pages/AuditPage";
import { DoorPage } from "@/pages/DoorPage";
import { EmergencyPage } from "@/pages/EmergencyPage";
import { ExamplePage } from "@/pages/ExamplePage";
import { HelpPage } from "@/pages/HelpPage";
import { MasterDataPage } from "@/pages/MasterDataPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { ParticipantsPage } from "@/pages/ParticipantsPage";
import { PermissionsPage } from "@/pages/PermissionsPage";
import { PrizesPage } from "@/pages/PrizesPage";
import { SponsorDetailPage, SponsorsPage } from "@/pages/SponsorsPage";

type Overview = components["schemas"]["BackofficeOverviewView"];
type SessionView = components["schemas"]["OperatorSessionView"];

function Centered({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 px-4 text-center">
      {children}
    </div>
  );
}

/**
 * Shell of the backoffice.
 *
 * The tenant is NOT chosen here. Like everywhere else it is resolved from the
 * called domain (concept 11.2); a tenant switch in the interface would be a
 * second, contradicting source of truth.
 *
 * Two requests, in this order and on purpose: the session first, so an
 * unauthenticated visitor gets the sign-in screen rather than an error, then the
 * overview once. Every screen reads from that one payload; only the pages of an
 * action load more (its doors, their bookings), because that belongs to one action.
 */
export function App(): ReactElement {
  const { pathname, search } = useLocation();
  const session = useResource<SessionView>(loadSession);
  const overview = useResource<Overview>(loadOverview);
  useForgetSignInMarkers(session.status === "ready");
  const recheck = session.reload;
  // Only a valid session is worth checking again. Once the check itself answers 401,
  // its own SESSION_LOST would trigger the next check, and the sign-in screen would
  // sit on an endless stream of requests.
  const sessionReady = session.status === "ready";
  useEffect(() => {
    if (!sessionReady) {
      return;
    }
    window.addEventListener(SESSION_LOST, recheck);
    return () => {
      window.removeEventListener(SESSION_LOST, recheck);
    };
  }, [recheck, sessionReady]);

  if (session.status === "loading" || overview.status === "loading") {
    return (
      <Centered>
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      </Centered>
    );
  }

  if (
    session.status === "unauthenticated" ||
    overview.status === "unauthenticated"
  ) {
    return <SignInScreen title="Backoffice" path={SIGN_IN_PATH} />;
  }

  // 403 cannot happen on these two: both are open to every operator role. Handled
  // rather than cast away, because the alternative is a non-null assertion that would
  // outlive the reason it was safe: the day one of them gets a narrower role, this
  // branch is what keeps the shell honest instead of blank.
  if (session.status === "forbidden" || overview.status === "forbidden") {
    return (
      <Centered>
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      </Centered>
    );
  }

  // `not_found` included: neither call names an action, so a 404 here is a broken route.
  if (session.status !== "ready" || overview.status !== "ready") {
    return (
      <Centered>
        <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
        <Button variant="outline" onClick={overview.reload}>
          Erneut versuchen
        </Button>
      </Centered>
    );
  }

  const data = overview.data;

  return (
    <div className="min-h-dvh text-foreground">
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
        href="#inhalt"
      >
        Zum Inhalt springen
      </a>
      <SessionRenewalNotice />

      {/* A slim bar on the accent band, in ink: grey or blue text would fail AA on #009de0.
          The one link to the help per page sits here, opened at the entry for this screen. */}
      <header className="mx-auto flex max-w-[96rem] flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2 lg:pt-6">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase">
            {session.data.tenant_name}
          </p>
          <p className="text-lg font-bold">Backoffice</p>
        </div>
        <div className="flex items-center gap-1">
          {session.data.platform_url && session.data.policy ? (
            <TenantMenu
              name={session.data.tenant_name}
              policy={session.data.policy}
              platformUrl={session.data.platform_url}
              granted={session.data.elements}
            />
          ) : null}
          <NotificationBell />
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/hilfe${helpAnchor(pathname, search)}`}>
              <CircleHelp aria-hidden="true" />
              Hilfe
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void signOut();
            }}
          >
            <LogOut aria-hidden="true" />
            Abmelden
          </Button>
        </div>
      </header>

      {/* One white card for navigation and content; edge to edge on a phone. */}
      <div className="mx-auto max-w-[96rem] py-4 sm:px-4 lg:py-6">
        <div className="flex flex-col gap-6 bg-card p-4 shadow-[var(--countdown-shadow-elevated)] sm:rounded-md sm:p-6 lg:flex-row">
          <SideNav granted={session.data.elements} superadmin={session.data.superadmin} />
          <main id="inhalt" className="min-w-0 flex-1">
        {/* One save bar for every page; links and the side navigation ask before unsaved changes go. */}
        <SaveBarProvider>
        <Routes>
          <Route
            path="/"
            element={
              <ActionsPage
                actions={data.actions}
                participants={data.participants}
                sponsors={data.sponsors}
                granted={session.data.elements}
                superadmin={session.data.superadmin}
                brandCatalog={session.data.brand_catalog}
                reloadOverview={overview.reload}
              />
            }
          />
          <Route
            path="/sponsoren"
            element={
              <SponsorsPage
                sponsors={data.sponsors}
                reloadOverview={overview.reload}
              />
            }
          />
          <Route
            path="/sponsoren/:sponsorId"
            element={
              <SponsorDetailPage
                sponsors={data.sponsors}
                reloadOverview={overview.reload}
                granted={session.data.elements}
              />
            }
          />
          <Route
            path="/aktionen/:actionId"
            element={
              <ActionPage
                actions={data.actions}
                participantBaseUrl={data.participant_base_url ?? null}
                sponsors={data.sponsors}
                granted={session.data.elements}
                superadmin={session.data.superadmin}
                reloadOverview={overview.reload}
              />
            }
          >
            <Route index element={<FieldPlan />} />
            <Route path="tuerchen/:position" element={<DoorPage />} />
            {/* The tab "Vorschau" is the plan's view "Kalender" now (D48); old links still land. */}
            <Route
              path="vorschau"
              element={<Navigate replace to={{ pathname: "..", search: "?ansicht=kalender" }} />}
            />
            <Route path="gestaltung" element={<ActionDesignPage />} />
            <Route path="einstellungen" element={<ActionSettingsPage />} />
            {/* The tab "Formular" is a view of "Gestaltung" now (D49); old links still land. */}
            <Route
              path="formular"
              element={
                <Navigate replace to={{ pathname: "../gestaltung", search: "?ansicht=formular" }} />
              }
            />
            <Route path="beispiel" element={<ExamplePage />} />
          </Route>
          <Route path="/gewinne" element={<PrizesPage actions={data.actions.filter(notArchived)} />} />
          <Route path="/not-aus" element={<EmergencyPage actions={data.actions.filter(notArchived)} />} />
          <Route
            path="/berechtigungen"
            element={<PermissionsPage superadmin={session.data.superadmin} />}
          />
          <Route
            path="/stammdaten"
            element={
              <MasterDataPage
                granted={session.data.elements}
                brandCatalog={session.data.brand_catalog}
              />
            }
          />
          <Route
            path="/teilnehmer"
            element={<ParticipantsPage granted={session.data.elements} />}
          />
          <Route path="/protokoll" element={<AuditPage />} />
          <Route path="/hilfe" element={<HelpPage roles={session.data.roles} />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </SaveBarProvider>
          </main>
        </div>
      </div>
    </div>
  );
}
